/**
 * Task Manager Panel — global floating button + quick-view drawer.
 *
 * Mounted once at the App root. Visible only to users in the curated
 * access list (admins, sub-admins, office staff, warehouse managers).
 *
 * Behaviour
 * ─────────
 * • Polls /api/task-panel/counts every 12 seconds.
 * • Plays a soft notification ping (Web Audio API, no asset) whenever
 *   the pending count goes UP since the previous poll.
 * • Single-click  → opens the quick drawer.
 * • Double-click  → navigates to /tasks (full-screen panel).
 * • Long-press / right-click on the badge resets the seen counter.
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, X, Plus, Clock, Check, CheckCircle2, AlertCircle, ListTodo, UserCog, Search, ChevronDown, Package, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 12000;

// ── tiny synth ping so we don't need an mp3 asset ────────────────────────
const playPing = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(880, now);
    o.frequency.exponentialRampToValueAtTime(1320, now + 0.15);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
    o.connect(g);
    g.connect(ctx.destination);
    o.start(now);
    o.stop(now + 0.5);
    setTimeout(() => { try { ctx.close(); } catch (e) { /* noop */ } }, 800);
  } catch (e) {
    /* user hasn't interacted yet — autoplay blocked, that's fine */
  }
};

// ── priority colours ──────────────────────────────────────────────────────
export const PRIORITY = {
  low:    { label: "Low",    color: "#F59E0B", bg: "rgba(245,158,11,0.12)" },  // orange
  medium: { label: "Medium", color: "#10B981", bg: "rgba(16,185,129,0.12)" },  // green
  high:   { label: "High",   color: "#EF4444", bg: "rgba(239,68,68,0.13)"  },  // red
};

const fmtDeadline = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tmrw = new Date(today); tmrw.setDate(tmrw.getDate() + 1);
    const sameDay = (a, b) =>
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate();
    const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (sameDay(d, today)) return `Today, ${time}`;
    if (sameDay(d, tmrw))  return `Tomorrow, ${time}`;
    return d.toLocaleDateString([], { day: "2-digit", month: "short" }) + ", " + time;
  } catch (e) { return ""; }
};

// ─────────────────────────────────────────────────────────────────────────
// Floating button
// ─────────────────────────────────────────────────────────────────────────
const FloatingTaskButton = () => {
  const navigate = useNavigate();
  // 2026-06-02 — pull the token from AuthContext so this component
  // doesn't race AuthProvider's `axios.defaults.headers.common` setter.
  // Previously the access fetch fired BEFORE AuthProvider's useEffect
  // had a chance to attach the bearer token (React runs child effects
  // before parent effects), causing the request to go anonymous on
  // first paint → 401 → `setAccess({has_access:false})` → button stayed
  // hidden FOREVER on production. Preview masked it because
  // React.StrictMode reruns effects, and the second run picked up the
  // token after AuthProvider had finished.
  const { token } = useAuth();
  const [access, setAccess] = useState(null);
  const [counts, setCounts] = useState({ total: 0, tasks_pending: 0, todos_pending: 0, overdue: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const lastCountRef = useRef(0);
  const clickTimerRef = useRef(null);

  // 1) Determine access — only after we have a token. Re-runs whenever
  // the token changes (login, logout, impersonation swap).
  useEffect(() => {
    if (!token) {
      setAccess(null);
      return;
    }
    let alive = true;
    (async () => {
      try {
        // Explicit Authorization header so we don't rely on the
        // axios.defaults race with AuthProvider's useEffect.
        const r = await axios.get(`${API}/task-panel/access`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setAccess(r.data);
      } catch {
        if (alive) setAccess({ has_access: false });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // 2) Poll counts every 12s — play ping on increase
  const refreshCounts = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/task-panel/counts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCounts(r.data);
      if (lastCountRef.current !== 0 && r.data.total > lastCountRef.current) {
        playPing();
      }
      lastCountRef.current = r.data.total;
    } catch (e) {
      /* swallow — counts are best-effort */
    }
  }, [token]);

  useEffect(() => {
    if (!access?.has_access) return;
    refreshCounts();
    const id = setInterval(refreshCounts, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [access?.has_access, refreshCounts]);

  // 3) Single-click → drawer, double-click → /tasks
  const handleClick = () => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
      navigate("/tasks");
      return;
    }
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      setDrawerOpen(true);
    }, 240);
  };

  if (!access?.has_access) return null;

  const overdue = counts.overdue > 0;
  const total = counts.total;

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="task-panel-floating-btn"
        title="Tasks (single-click for quick view, double-click for full panel)"
        className="fixed bottom-5 right-3 z-[60] group"
        style={{ animation: overdue ? "tpPulse 1.6s ease-in-out infinite" : undefined }}
      >
        <span
          className="flex items-center justify-center w-14 h-14 rounded-full shadow-xl border border-white/15 backdrop-blur-sm transition-transform group-hover:scale-105"
          style={{
            background:
              "linear-gradient(135deg, #ef4444 0%, #f97316 50%, #f59e0b 100%)",
            boxShadow:
              "0 14px 30px -10px rgba(239,68,68,0.55), 0 0 0 4px rgba(239,68,68,0.10)",
          }}
        >
          <ListTodo className="w-6 h-6 text-white drop-shadow" />
        </span>
        {total > 0 && (
          <span
            data-testid="task-panel-badge"
            className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-white text-[11px] font-bold flex items-center justify-center border-2"
            style={{ color: overdue ? "#dc2626" : "#0f172a", borderColor: overdue ? "#dc2626" : "#0f172a" }}
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {drawerOpen && (
        <TaskQuickDrawer
          access={access}
          counts={counts}
          onClose={() => { setDrawerOpen(false); refreshCounts(); }}
          onRefresh={refreshCounts}
          onOpenFull={() => { setDrawerOpen(false); navigate("/tasks"); }}
        />
      )}

      <style>{`
        @keyframes tpPulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.05); }
        }
      `}</style>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Quick drawer (single-click)
// ─────────────────────────────────────────────────────────────────────────
const TaskQuickDrawer = ({ access, counts, onClose, onRefresh, onOpenFull }) => {
  const [tab, setTab] = useState("assigned");  // 'assigned' | 'todos' | 'by-me' | 'all'
  const [tasks, setTasks] = useState([]);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showTodoCreate, setShowTodoCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === "assigned") {
        const r = await axios.get(`${API}/task-panel/tasks?scope=mine`);
        setTasks(r.data.tasks || []);
      } else if (tab === "by-me") {
        const r = await axios.get(`${API}/task-panel/tasks?scope=by-me`);
        setTasks(r.data.tasks || []);
      } else if (tab === "all") {
        const r = await axios.get(`${API}/task-panel/tasks?scope=all`);
        setTasks(r.data.tasks || []);
      } else if (tab === "todos") {
        const r = await axios.get(`${API}/task-panel/todos`);
        setTodos(r.data.todos || []);
      }
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const refreshAll = () => { load(); onRefresh(); };

  return (
    <>
      <div
        onClick={onClose}
        className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm"
        data-testid="task-panel-overlay"
      />
      <aside
        className="fixed top-0 right-0 z-[71] h-full w-full sm:w-[440px] bg-[#0f172a] text-white border-l border-white/10 shadow-2xl flex flex-col"
        data-testid="task-panel-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-white/45">Task Panel</p>
            <p className="text-sm font-semibold">{access.name} · <span className="text-white/55">{access.role}</span></p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onOpenFull}
              className="text-xs px-2.5 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/80"
              data-testid="task-panel-open-full"
            >
              Full View
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-white/10"
              data-testid="task-panel-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 px-3 pt-3 flex-wrap">
          <Tab active={tab === "assigned"} onClick={() => setTab("assigned")}>
            Assigned to me <span className="ml-1 text-white/40">{counts.tasks_pending}</span>
          </Tab>
          <Tab active={tab === "todos"} onClick={() => setTab("todos")}>
            My To-Do <span className="ml-1 text-white/40">{counts.todos_pending}</span>
          </Tab>
          {access.is_admin && (
            <Tab active={tab === "by-me"} onClick={() => setTab("by-me")}>
              By me
            </Tab>
          )}
          {access.is_admin && (
            <Tab active={tab === "all"} onClick={() => setTab("all")}>
              All Tasks
            </Tab>
          )}
        </div>

        {/* Create buttons */}
        <div className="flex items-center gap-2 px-5 pt-3 pb-2">
          {access.is_admin && tab !== "todos" && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-500 font-medium"
              data-testid="task-panel-new-task-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New Task
            </button>
          )}
          {tab === "todos" && (
            <button
              type="button"
              onClick={() => setShowTodoCreate(true)}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 font-medium"
              data-testid="task-panel-new-todo-btn"
            >
              <Plus className="w-3.5 h-3.5" /> New To-Do
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-3 pb-5 pt-1">
          {loading ? (
            <div className="flex justify-center pt-10">
              <Loader2 className="w-5 h-5 animate-spin text-white/50" />
            </div>
          ) : tab === "todos" ? (
            todos.length === 0 ? (
              <EmptyState icon={<ListTodo className="w-8 h-8" />} text="No personal to-dos yet" />
            ) : (
              todos.map((t) => (
                <TodoCard key={t.todo_id} todo={t} onChanged={refreshAll} />
              ))
            )
          ) : tasks.length === 0 ? (
            <EmptyState
              icon={<CheckCircle2 className="w-8 h-8" />}
              text={
                tab === "assigned"
                  ? "No tasks assigned to you"
                  : tab === "by-me"
                  ? "You haven't assigned any tasks"
                  : "No tasks in the system"
              }
            />
          ) : (
            tasks.map((t) => (
              <TaskCard
                key={t.task_id}
                task={t}
                meMobile={access.mobile}
                onChanged={refreshAll}
                isAdmin={access.is_admin}
                showAssignee={tab === "all" || tab === "by-me"}
              />
            ))
          )}
        </div>
      </aside>

      {showCreate && (
        <TaskCreateDialog onClose={() => setShowCreate(false)} onSaved={refreshAll} />
      )}
      {showTodoCreate && (
        <TodoCreateDialog onClose={() => setShowTodoCreate(false)} onSaved={refreshAll} />
      )}
    </>
  );
};

const Tab = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-[12px] px-3 py-2 rounded-md font-medium transition-colors ${
      active ? "bg-white/10 text-white" : "text-white/55 hover:text-white/80"
    }`}
  >
    {children}
  </button>
);

const EmptyState = ({ icon, text }) => (
  <div className="flex flex-col items-center justify-center pt-16 text-white/40">
    {icon}
    <p className="mt-3 text-sm">{text}</p>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Task & Todo cards
// ─────────────────────────────────────────────────────────────────────────
const TaskCard = ({ task, meMobile, onChanged, isAdmin, showAssignee = false }) => {
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const p = PRIORITY[task.priority] || PRIORITY.medium;
  const isAssignee = (task.assigned_to || []).includes(meMobile);
  const overdue = task.deadline && new Date(task.deadline) < new Date() && task.status !== "completed";

  const setStatus = async (status) => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/task-panel/tasks/${task.task_id}`, { status });
      onChanged();
    } finally { setBusy(false); }
  };

  const assigneeNames = task.assigned_to_names || [];
  const assigneeLabel = assigneeNames.length === 0
    ? null
    : assigneeNames.length === 1
      ? assigneeNames[0]
      : `${assigneeNames[0]} +${assigneeNames.length - 1}`;

  // ── Order chip: hover preview + click navigates to current stage ────────
  // The universal route `/task-manager/order/:orderId` auto-derives the
  // correct sub-stage (sorting / packing / dispatch / billing / completed)
  // from the order's status, so a single link covers every lifecycle phase.
  const [hoverOrder, setHoverOrder] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const hoverCacheRef = useRef({}); // order_id → preview payload
  const hoverTimerRef = useRef(null);
  const orderId = task.linked_order_id;
  const custId = task.linked_customer_id;
  const partJp = task.linked_jp_part_no;

  // ── Part chip: hover preview (with image) + click goes to admin/products
  const [hoverPart, setHoverPart] = useState(null);
  const [partAnchor, setPartAnchor] = useState(null);
  const partCacheRef = useRef({});
  const partHoverTimerRef = useRef(null);
  const [zoomSrc, setZoomSrc] = useState(null);

  const fetchOrderPreview = useCallback(async (oid) => {
    if (!oid) return null;
    if (hoverCacheRef.current[oid]) return hoverCacheRef.current[oid];
    try {
      const r = await axios.get(`${API}/task-panel/pickers/order/${encodeURIComponent(oid)}`);
      hoverCacheRef.current[oid] = r.data;
      return r.data;
    } catch (e) {
      hoverCacheRef.current[oid] = { order_id: oid, status: "—", item_count: 0, preview_items: [], _error: true };
      return hoverCacheRef.current[oid];
    }
  }, []);

  const onOrderEnter = (e) => {
    if (!orderId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const anchor = { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, height: rect.height, width: rect.width };
    setHoverAnchor(anchor);
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(async () => {
      const data = await fetchOrderPreview(orderId);
      if (data) setHoverOrder(data);
    }, 120);
  };
  const onOrderLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoverOrder(null);
    setHoverAnchor(null);
  };
  const openOrder = async (e) => {
    e.stopPropagation();
    if (!orderId) return;
    // Phase 3B (2026-10-06) — BOM Pick tasks deep-link to the staff
    // picking panel (reuses BomWoPickingPanel, Scanning-Bible compliant).
    if (task?.task_type === "bom_pick" && task?.bom_wo_id) {
      navigate(`/bom-picking/${encodeURIComponent(task.bom_wo_id)}`);
      return;
    }
    // Reuse the hover-cached payload when available; otherwise fetch once
    // so the navigation lands on the *correct* lifecycle page (pre-pipeline
    // orders → /admin/orders, in-pipeline → /task-manager/order).
    let data = hoverCacheRef.current[orderId];
    if (!data || data._error) data = await fetchOrderPreview(orderId);
    const route = (data && data.route) || `/task-manager/order/${encodeURIComponent(orderId)}`;
    navigate(route);
  };
  const openCustomer = (e) => {
    e.stopPropagation();
    if (!custId) return;
    navigate(`/admin/customers?q=${encodeURIComponent(custId)}`);
  };

  // Part chip handlers
  const fetchPartPreview = useCallback(async (jp) => {
    if (!jp) return null;
    if (partCacheRef.current[jp]) return partCacheRef.current[jp];
    try {
      const r = await axios.get(`${API}/task-panel/pickers/part/${encodeURIComponent(jp)}`);
      partCacheRef.current[jp] = r.data;
      return r.data;
    } catch (e) {
      partCacheRef.current[jp] = { jp_part_no: jp, _error: true };
      return partCacheRef.current[jp];
    }
  }, []);

  const onPartEnter = (e) => {
    if (!partJp) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setPartAnchor({ top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, height: rect.height, width: rect.width });
    if (partHoverTimerRef.current) clearTimeout(partHoverTimerRef.current);
    partHoverTimerRef.current = setTimeout(async () => {
      const data = await fetchPartPreview(partJp);
      if (data && !data._error) setHoverPart(data);
    }, 120);
  };
  const onPartLeave = () => {
    if (partHoverTimerRef.current) clearTimeout(partHoverTimerRef.current);
    setHoverPart(null);
    setPartAnchor(null);
  };
  const openPart = (e) => {
    e.stopPropagation();
    if (!partJp) return;
    navigate(`/admin/products?q=${encodeURIComponent(partJp)}`);
  };

  return (
    <div
      className={`p-3 mb-2 rounded-lg border ${task.status === "completed" ? "opacity-60" : ""}`}
      style={{
        borderColor: overdue ? "rgba(239,68,68,0.35)" : "rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.03)",
      }}
      data-testid={`task-card-${task.task_id}`}
    >
      <div className="flex items-start gap-2">
        <span
          className="mt-1.5 w-2 h-2 rounded-full shrink-0"
          style={{ background: p.color }}
          title={p.label + " priority"}
        />
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${task.status === "completed" ? "line-through" : ""}`}>
            {task.title}
          </p>
          {task.description && (
            <p className="text-xs text-white/55 mt-0.5">{task.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px] text-white/55">
            <span>by {task.assigned_by_name}</span>
            {showAssignee && assigneeLabel && (
              <span title={assigneeNames.join(", ")} className="text-white/70">
                → {assigneeLabel}
              </span>
            )}
            {!showAssignee && assigneeNames.length > 1 && (
              <span>→ {assigneeNames.length} people</span>
            )}
            {task.deadline && (
              <span className={overdue ? "text-red-400 font-medium flex items-center gap-1" : "flex items-center gap-1"}>
                <Clock className="w-3 h-3" /> {fmtDeadline(task.deadline)}
                {overdue && <AlertCircle className="w-3 h-3" />}
              </span>
            )}
            {orderId && (
              <button
                type="button"
                onClick={openOrder}
                onMouseEnter={onOrderEnter}
                onMouseLeave={onOrderLeave}
                onFocus={onOrderEnter}
                onBlur={onOrderLeave}
                className="inline-flex items-center gap-1 text-sky-300 hover:text-sky-200 underline decoration-dotted decoration-sky-400/40 underline-offset-2"
                data-testid={`task-card-order-link-${task.task_id}`}
                title="Open this order — auto-routes to its current stage"
              >
                Order: {orderId}
              </button>
            )}
            {custId && (
              <button
                type="button"
                onClick={openCustomer}
                className="inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 underline decoration-dotted decoration-emerald-400/40 underline-offset-2"
                data-testid={`task-card-customer-link-${task.task_id}`}
                title="Open this customer in the admin directory"
              >
                Cust: {custId}
              </button>
            )}
            {partJp && (
              <button
                type="button"
                onClick={openPart}
                onMouseEnter={onPartEnter}
                onMouseLeave={onPartLeave}
                onFocus={onPartEnter}
                onBlur={onPartLeave}
                className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 underline decoration-dotted decoration-amber-400/40 underline-offset-2"
                data-testid={`task-card-part-link-${task.task_id}`}
                title="Open this SKU in the admin product list"
              >
                <Package className="w-3 h-3" />
                Part: {partJp}
              </button>
            )}
            <span
              className="px-1.5 py-[1px] rounded text-[10px] uppercase tracking-wider"
              style={{ background: p.bg, color: p.color }}
            >
              {p.label}
            </span>
            <span className="text-white/40">· {task.status.replace("_", " ")}</span>
          </div>
        </div>
      </div>
      {hoverOrder && hoverAnchor && createPortal(
        <OrderHoverPreview order={hoverOrder} anchor={hoverAnchor} />,
        document.body
      )}
      {hoverPart && partAnchor && createPortal(
        <PartHoverPreview
          part={hoverPart}
          anchor={partAnchor}
          onImageClick={(src) => setZoomSrc(src)}
        />,
        document.body
      )}
      {zoomSrc && (
        <PartImageZoom
          src={zoomSrc}
          alt={hoverPart?.part_name}
          onClose={() => setZoomSrc(null)}
        />
      )}

      {(isAssignee || isAdmin) && task.status !== "completed" && (
        <div className="flex gap-1.5 mt-2">
          {task.status === "pending" && (
            <button
              type="button"
              disabled={busy}
              onClick={() => setStatus("in_progress")}
              className="text-[11px] px-2 py-1 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25"
              data-testid={`task-start-${task.task_id}`}
            >
              Start
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => setStatus("completed")}
            className="text-[11px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 flex items-center gap-1"
            data-testid={`task-complete-${task.task_id}`}
          >
            <Check className="w-3 h-3" /> Done
          </button>
        </div>
      )}
    </div>
  );
};

const TodoCard = ({ todo, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const p = PRIORITY[todo.priority] || PRIORITY.medium;
  const overdue = todo.deadline && new Date(todo.deadline) < new Date() && todo.status === "pending";

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.patch(`${API}/task-panel/todos/${todo.todo_id}`, {
        status: todo.status === "completed" ? "pending" : "completed",
      });
      onChanged();
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/task-panel/todos/${todo.todo_id}`);
      onChanged();
    } finally { setBusy(false); }
  };

  return (
    <div
      className={`p-3 mb-2 rounded-lg border flex items-start gap-2 ${todo.status === "completed" ? "opacity-60" : ""}`}
      style={{
        borderColor: overdue ? "rgba(239,68,68,0.35)" : "rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.03)",
      }}
      data-testid={`todo-card-${todo.todo_id}`}
    >
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className="mt-0.5 w-4 h-4 rounded border border-white/30 flex items-center justify-center hover:border-white/60"
        data-testid={`todo-toggle-${todo.todo_id}`}
      >
        {todo.status === "completed" && <Check className="w-3 h-3 text-emerald-400" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${todo.status === "completed" ? "line-through" : ""}`}>{todo.title}</p>
        {todo.description && <p className="text-xs text-white/55 mt-0.5">{todo.description}</p>}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-[11px] text-white/55">
          {todo.deadline && (
            <span className={overdue ? "text-red-400 font-medium flex items-center gap-1" : "flex items-center gap-1"}>
              <Clock className="w-3 h-3" /> {fmtDeadline(todo.deadline)}
            </span>
          )}
          <span
            className="px-1.5 py-[1px] rounded text-[10px] uppercase tracking-wider"
            style={{ background: p.bg, color: p.color }}
          >
            {p.label}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={remove}
        className="text-white/40 hover:text-red-400 p-1"
        data-testid={`todo-delete-${todo.todo_id}`}
        title="Delete to-do"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Create dialogs
// ─────────────────────────────────────────────────────────────────────────
const TaskCreateDialog = ({ onClose, onSaved }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    title: "", description: "", assigned_to: [], priority: "medium",
    deadline: "", linked_order_id: "", linked_customer_id: "", linked_jp_part_no: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/task-panel/assignable-users`).then((r) => setUsers(r.data.users || []));
  }, []);

  const toggleAssignee = (m) => {
    setForm((f) => ({
      ...f,
      assigned_to: f.assigned_to.includes(m)
        ? f.assigned_to.filter((x) => x !== m)
        : [...f.assigned_to, m],
    }));
  };

  const submit = async () => {
    setErr("");
    if (!form.title.trim()) return setErr("Title is required");
    if (form.assigned_to.length === 0) return setErr("Pick at least one assignee");
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim() || "",
        assigned_to: form.assigned_to,
        priority: form.priority,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        linked_order_id: form.linked_order_id.trim() || null,
        linked_customer_id: form.linked_customer_id.trim() || null,
        linked_jp_part_no: form.linked_jp_part_no.trim() || null,
      };
      await axios.post(`${API}/task-panel/tasks`, payload);
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f172a] text-white border border-white/10 rounded-xl max-w-lg w-full p-5 shadow-2xl max-h-[90vh] overflow-y-auto"
        data-testid="task-create-dialog"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-white">Assign a new task</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {err && <p className="mb-3 text-xs text-red-400">{err}</p>}
        <Field label="Title">
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-white/35 focus:outline-none focus:border-red-500/60"
            placeholder="e.g. Reconcile Tally for ORD-1234"
            data-testid="task-create-title"
          />
        </Field>
        <Field label="Description (optional)">
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-white/35 focus:outline-none focus:border-red-500/60"
            placeholder="Any extra notes"
          />
        </Field>
        <Field label="Assign to">
          <div className="max-h-44 overflow-y-auto border border-white/10 rounded-md p-2 bg-white/5 space-y-1">
            {users.map((u) => (
              <label
                key={u.mobile}
                className="flex items-center gap-2 text-xs text-white cursor-pointer hover:bg-white/10 px-2 py-1 rounded"
              >
                <input
                  type="checkbox"
                  checked={form.assigned_to.includes(u.mobile)}
                  onChange={() => toggleAssignee(u.mobile)}
                  className="accent-red-500"
                  data-testid={`task-create-assignee-${u.mobile}`}
                />
                <span className="font-medium text-white">{u.name}</span>
                <span className="text-white/55">· {u.role}</span>
              </label>
            ))}
          </div>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500/60"
              data-testid="task-create-priority"
            >
              <option value="low" className="bg-[#0f172a]">Low</option>
              <option value="medium" className="bg-[#0f172a]">Medium</option>
              <option value="high" className="bg-[#0f172a]">High</option>
            </select>
          </Field>
          <Field label="Deadline (optional)">
            <input
              type="datetime-local"
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-red-500/60"
              data-testid="task-create-deadline"
            />
          </Field>
        </div>
        <Field label="Link to a customer / order (optional)">
          <CustomerOrderPicker
            customerId={form.linked_customer_id}
            orderId={form.linked_order_id}
            onChange={({ customer_id, order_id }) =>
              setForm({ ...form, linked_customer_id: customer_id || "", linked_order_id: order_id || "" })
            }
          />
        </Field>
        <Field label="Link to a part / SKU (optional)">
          <PartPicker
            jpPartNo={form.linked_jp_part_no}
            onChange={(jp) => setForm({ ...form, linked_jp_part_no: jp || "" })}
          />
        </Field>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="text-sm px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 font-medium text-white disabled:opacity-50"
            data-testid="task-create-submit"
          >
            {saving ? "Saving…" : "Assign Task"}
          </button>
        </div>
      </div>
    </div>
  );
};

const TodoCreateDialog = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({ title: "", description: "", priority: "medium", deadline: "" });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    if (!form.title.trim()) return setErr("Title is required");
    setSaving(true);
    try {
      await axios.post(`${API}/task-panel/todos`, {
        title: form.title.trim(),
        description: form.description.trim() || "",
        priority: form.priority,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f172a] text-white border border-white/10 rounded-xl max-w-md w-full p-5 shadow-2xl"
        data-testid="todo-create-dialog"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-base font-semibold text-white">New personal to-do</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {err && <p className="mb-3 text-xs text-red-400">{err}</p>}
        <Field label="Title">
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-white/35 focus:outline-none focus:border-emerald-500/60"
            data-testid="todo-create-title"
          />
        </Field>
        <Field label="Notes (optional)">
          <textarea
            rows={2}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder-white/35 focus:outline-none focus:border-emerald-500/60"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Priority">
            <select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/60"
              data-testid="todo-create-priority"
            >
              <option value="low" className="bg-[#0f172a]">Low</option>
              <option value="medium" className="bg-[#0f172a]">Medium</option>
              <option value="high" className="bg-[#0f172a]">High</option>
            </select>
          </Field>
          <Field label="Deadline (optional)">
            <input
              type="datetime-local"
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-emerald-500/60"
              data-testid="todo-create-deadline"
            />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-md bg-white/10 hover:bg-white/20 text-white">Cancel</button>
          <button
            onClick={submit}
            disabled={saving}
            className="text-sm px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 font-medium text-white disabled:opacity-50"
            data-testid="todo-create-submit"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div className="mb-3">
    <label className="block text-[11px] uppercase tracking-wider text-white/45 mb-1">{label}</label>
    {children}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────
// Customer + Order picker — dynamic search with hover preview
// ─────────────────────────────────────────────────────────────────────────
const useDebounced = (value, ms = 250) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return v;
};

const fmtAmount = (n) => {
  const num = Number(n || 0);
  if (!num) return "—";
  return "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
};

// Floating preview rendered via Portal to escape any clipping parent.
// `anchor` is a viewport-relative DOMRect-like {top,left,right,bottom,height}.
const OrderHoverPreview = ({ order, anchor }) => {
  const PANEL_W = 288;   // w-72
  const PANEL_H_EST = 220;
  const GAP = 8;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  // Prefer right side of the row; fall back to left if there's no room
  let left = anchor.right + GAP;
  if (left + PANEL_W + 8 > vw) {
    left = anchor.left - PANEL_W - GAP;
  }
  if (left < 8) left = 8;
  let top = anchor.top;
  if (top + PANEL_H_EST + 8 > vh) {
    top = Math.max(8, vh - PANEL_H_EST - 8);
  }

  return (
    <div
      className="fixed z-[200] w-72 max-h-80 overflow-y-auto bg-[#0b1224] border border-white/15 rounded-md shadow-2xl p-3 pointer-events-none"
      style={{ top, left }}
      data-testid="task-create-order-hover-preview"
    >
      <p className="text-xs font-semibold text-white">{order.order_id}</p>
      <p className="text-[11px] text-white/55 mb-2">
        {order.status || "—"}
        {` · ${order.item_count || 0} item${order.item_count === 1 ? "" : "s"}`}
        {` · ${"₹" + Number(order.total_amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
      </p>
      {(order.preview_items || []).length === 0 ? (
        <p className="text-[11px] text-white/45 italic">No items recorded on this order</p>
      ) : (
        <>
          <p className="text-[10px] uppercase tracking-wider text-white/45 mb-1.5">
            Items {order.item_count > 5 ? `(top 5 of ${order.item_count})` : ""}
          </p>
          <ul className="space-y-1">
            {(order.preview_items || []).map((it, i) => (
              <li key={i} className="flex items-start gap-2 text-[11px]">
                <span className="text-white/55 shrink-0 font-mono">{it.jp || "—"}</span>
                <span className="text-white/85 truncate flex-1">{it.name || ""}</span>
                <span className="text-white/55 shrink-0">× {it.qty}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const CustomerOrderPicker = ({ customerId, orderId, onChange }) => {
  // ── customer state
  const [custQuery, setCustQuery] = useState("");
  const debCust = useDebounced(custQuery, 220);
  const [custResults, setCustResults] = useState([]);
  const [custOpen, setCustOpen] = useState(false);
  const [selectedCust, setSelectedCust] = useState(null);
  const [custLoading, setCustLoading] = useState(false);
  const custWrapRef = useRef(null);

  // ── order state
  const [orders, setOrders] = useState([]);
  const [orderQuery, setOrderQuery] = useState("");
  const [orderOpen, setOrderOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [hoveredOrder, setHoveredOrder] = useState(null);
  const [hoverAnchor, setHoverAnchor] = useState(null); // {top, left, height} from getBoundingClientRect
  const orderWrapRef = useRef(null);

  // Customer search
  useEffect(() => {
    let alive = true;
    if (!debCust.trim() || selectedCust) {
      setCustResults([]);
      return;
    }
    setCustLoading(true);
    axios.get(`${API}/task-panel/pickers/customers`, { params: { q: debCust } })
      .then((r) => { if (alive) setCustResults(r.data.customers || []); })
      .catch(() => { if (alive) setCustResults([]); })
      .finally(() => { if (alive) setCustLoading(false); });
    return () => { alive = false; };
  }, [debCust, selectedCust]);

  // Load orders when customer chosen
  useEffect(() => {
    let alive = true;
    if (!selectedCust) {
      setOrders([]);
      setSelectedOrder(null);
      return;
    }
    setOrdersLoading(true);
    axios.get(`${API}/task-panel/pickers/customers/${selectedCust.customer_id}/orders`)
      .then((r) => { if (alive) setOrders(r.data.orders || []); })
      .catch(() => { if (alive) setOrders([]); })
      .finally(() => { if (alive) setOrdersLoading(false); });
    return () => { alive = false; };
  }, [selectedCust]);

  // Close dropdowns on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (custWrapRef.current && !custWrapRef.current.contains(e.target)) setCustOpen(false);
      if (orderWrapRef.current && !orderWrapRef.current.contains(e.target)) setOrderOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pickCustomer = (c) => {
    setSelectedCust(c);
    setCustQuery("");
    setCustOpen(false);
    onChange({ customer_id: c.customer_id, order_id: null });
  };

  const clearCustomer = () => {
    setSelectedCust(null);
    setCustQuery("");
    setCustResults([]);
    setSelectedOrder(null);
    onChange({ customer_id: null, order_id: null });
  };

  const pickOrder = (o) => {
    setSelectedOrder(o);
    setOrderQuery("");
    setOrderOpen(false);
    onChange({ customer_id: selectedCust?.customer_id || null, order_id: o.order_id });
  };

  const clearOrder = () => {
    setSelectedOrder(null);
    setOrderQuery("");
    onChange({ customer_id: selectedCust?.customer_id || null, order_id: null });
  };

  const filteredOrders = orders.filter((o) => {
    const q = orderQuery.trim().toLowerCase();
    if (!q) return true;
    return (o.order_id || "").toLowerCase().includes(q)
      || (o.status || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-2">
      {/* CUSTOMER ROW */}
      <div ref={custWrapRef} className="relative">
        {selectedCust ? (
          <div
            className="flex items-center justify-between gap-2 bg-white/5 border border-white/15 rounded-md px-3 py-2"
            data-testid="task-create-selected-customer"
          >
            <div className="flex items-center gap-2 min-w-0">
              <User className="w-3.5 h-3.5 text-white/55 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm text-white truncate">
                  {selectedCust.name || selectedCust.company_name || selectedCust.customer_id}
                </p>
                <p className="text-[11px] text-white/55 truncate">
                  {selectedCust.customer_id}
                  {selectedCust.city ? ` · ${selectedCust.city}` : ""}
                  {selectedCust.mobile ? ` · ${selectedCust.mobile}` : ""}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={clearCustomer}
              className="text-white/45 hover:text-white"
              data-testid="task-create-clear-customer"
              title="Clear customer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
            <input
              type="text"
              value={custQuery}
              onChange={(e) => { setCustQuery(e.target.value); setCustOpen(true); }}
              onFocus={() => setCustOpen(true)}
              placeholder="Search customer by ID, name, company or mobile…"
              className="w-full bg-white/5 border border-white/10 rounded-md pl-8 pr-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-red-500/60"
              data-testid="task-create-customer-search"
              autoComplete="off"
            />
            {custOpen && (custLoading || custResults.length > 0 || debCust.trim()) && (
              <div
                className="absolute z-[90] mt-1 left-0 right-0 max-h-64 overflow-y-auto bg-[#0b1224] border border-white/15 rounded-md shadow-xl"
                data-testid="task-create-customer-dropdown"
              >
                {custLoading ? (
                  <div className="flex items-center justify-center py-3 text-white/50">
                    <Loader2 className="w-4 h-4 animate-spin" />
                  </div>
                ) : custResults.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-white/45">No customers found</p>
                ) : (
                  custResults.map((c) => (
                    <button
                      type="button"
                      key={c.customer_id}
                      onClick={() => pickCustomer(c)}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-b-0"
                      data-testid={`task-create-customer-opt-${c.customer_id}`}
                    >
                      <p className="text-sm text-white truncate">
                        {c.name || c.company_name || c.customer_id}
                      </p>
                      <p className="text-[11px] text-white/50 truncate">
                        {c.customer_id}
                        {c.company_name && (c.company_name !== c.name) ? ` · ${c.company_name}` : ""}
                        {c.city ? ` · ${c.city}` : ""}
                        {c.mobile ? ` · ${c.mobile}` : ""}
                      </p>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ORDER ROW (only shown after a customer is chosen) */}
      {selectedCust && (
        <div ref={orderWrapRef} className="relative">
          {selectedOrder ? (
            <div data-testid="task-create-selected-order">
              <div className="flex items-center justify-between gap-2 bg-white/5 border border-white/15 rounded-md px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Package className="w-3.5 h-3.5 text-white/55 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{selectedOrder.order_id}</p>
                    <p className="text-[11px] text-white/55 truncate">
                      {selectedOrder.status || "—"}
                      {` · ${selectedOrder.item_count || 0} item${selectedOrder.item_count === 1 ? "" : "s"}`}
                      {` · ${fmtAmount(selectedOrder.total_amount)}`}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearOrder}
                  className="text-white/45 hover:text-white"
                  data-testid="task-create-clear-order"
                  title="Clear order"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {/* Inline preview — always visible for the chosen order */}
              {(selectedOrder.preview_items || []).length > 0 && (
                <div className="mt-1 bg-[#0b1224] border border-white/10 rounded-md p-2.5">
                  <p className="text-[10px] uppercase tracking-wider text-white/45 mb-1.5">
                    Order items {selectedOrder.item_count > 5 ? `(top 5 of ${selectedOrder.item_count})` : ""}
                  </p>
                  <ul className="space-y-1">
                    {(selectedOrder.preview_items || []).map((it, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px]">
                        <span className="text-white/55 shrink-0 font-mono">{it.jp || "—"}</span>
                        <span className="text-white/80 truncate flex-1">{it.name || ""}</span>
                        <span className="text-white/55 shrink-0">× {it.qty}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
              <input
                type="text"
                value={orderQuery}
                onChange={(e) => { setOrderQuery(e.target.value); setOrderOpen(true); }}
                onFocus={() => setOrderOpen(true)}
                placeholder={ordersLoading ? "Loading orders…" : `Search orders for ${selectedCust.customer_id}…`}
                className="w-full bg-white/5 border border-white/10 rounded-md pl-8 pr-8 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-red-500/60"
                data-testid="task-create-order-search"
                autoComplete="off"
              />
              <ChevronDown
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none"
              />
              {orderOpen && (
                <div
                  className="absolute z-[90] mt-1 left-0 right-0 max-h-72 overflow-y-auto bg-[#0b1224] border border-white/15 rounded-md shadow-xl"
                  data-testid="task-create-order-dropdown"
                  onMouseLeave={() => { setHoveredOrder(null); setHoverAnchor(null); }}
                >
                  {ordersLoading ? (
                    <div className="flex items-center justify-center py-3 text-white/50">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  ) : filteredOrders.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-white/45">
                      {orders.length === 0 ? "No orders for this customer" : "No matching orders"}
                    </p>
                  ) : (
                    filteredOrders.map((o) => {
                      const isHovered = hoveredOrder && hoveredOrder.order_id === o.order_id;
                      const onEnter = (e) => {
                        setHoveredOrder(o);
                        const r = e.currentTarget.getBoundingClientRect();
                        setHoverAnchor({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, height: r.height, width: r.width });
                      };
                      return (
                        <button
                          type="button"
                          key={o.order_id}
                          onClick={() => pickOrder(o)}
                          onMouseEnter={onEnter}
                          onFocus={onEnter}
                          className={`w-full text-left px-3 py-2 border-b border-white/5 last:border-b-0 ${isHovered ? "bg-white/5" : "hover:bg-white/5"}`}
                          data-testid={`task-create-order-opt-${o.order_id}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm text-white truncate">{o.order_id}</p>
                            <p className="text-[11px] text-white/55 shrink-0">{fmtAmount(o.total_amount)}</p>
                          </div>
                          <p className="text-[11px] text-white/50 truncate">
                            {o.status || "—"}
                            {` · ${o.item_count || 0} item${o.item_count === 1 ? "" : "s"}`}
                            {o.created_at ? ` · ${new Date(o.created_at).toLocaleDateString()}` : ""}
                          </p>
                        </button>
                      );
                    })
                  )}
                </div>
              )}
              {/* Hover preview rendered via Portal so dialog overflow cannot clip it. */}
              {orderOpen && hoveredOrder && hoverAnchor && createPortal(
                <OrderHoverPreview order={hoveredOrder} anchor={hoverAnchor} />,
                document.body
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FloatingTaskButton;

// ─────────────────────────────────────────────────────────────────────────
// Part picker — search for a SKU by JP / OE / name to link to a task.
// Renders a thumbnail in the chip + dropdown rows.
// ─────────────────────────────────────────────────────────────────────────
const _BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const partImg = (p) =>
  p && p.image ? (p.image.startsWith("http") ? p.image : `${_BACKEND}${p.image}`) : null;

const PartImageZoom = ({ src, alt, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div
      className="fixed inset-0 z-[300] bg-black/85 flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="part-image-zoom"
    >
      <button
        type="button"
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
        onClick={onClose}
        data-testid="part-image-zoom-close"
      >
        <X className="w-5 h-5" />
      </button>
      <img
        src={src}
        alt={alt || "Part"}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[90vw] object-contain rounded shadow-2xl"
      />
    </div>,
    document.body
  );
};

const PartHoverPreview = ({ part, anchor, onImageClick }) => {
  const PANEL_W = 320;
  const PANEL_H_EST = 320;
  const GAP = 8;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  let left = anchor.right + GAP;
  if (left + PANEL_W + 8 > vw) left = anchor.left - PANEL_W - GAP;
  if (left < 8) left = 8;
  let top = anchor.top;
  if (top + PANEL_H_EST + 8 > vh) top = Math.max(8, vh - PANEL_H_EST - 8);
  const img = partImg(part);
  return (
    <div
      className="fixed z-[200] w-80 bg-[#0b1224] border border-white/15 rounded-md shadow-2xl p-3"
      style={{ top, left }}
      data-testid="task-card-part-hover-preview"
    >
      <div className="flex gap-3">
        {img ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onImageClick && onImageClick(img); }}
            className="shrink-0 w-20 h-20 rounded bg-white/5 border border-white/10 overflow-hidden hover:border-sky-400/60 group/img relative"
            title="Click to zoom"
            data-testid="task-card-part-hover-image"
          >
            <img src={img} alt={part.part_name || part.jp_part_no} className="w-full h-full object-contain" />
            <span className="absolute inset-0 bg-black/30 opacity-0 group-hover/img:opacity-100 flex items-center justify-center transition-opacity">
              <Search className="w-4 h-4 text-white" />
            </span>
          </button>
        ) : (
          <div className="shrink-0 w-20 h-20 rounded bg-white/5 border border-white/10 flex items-center justify-center text-white/30 text-[10px]">
            No image
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">
            {part.part_name || part.jp_part_no}
          </p>
          <p className="text-[11px] text-white/60 truncate">
            JP: <span className="font-mono">{part.jp_part_no}</span>
          </p>
          {part.legacy_jp_part_no && part.legacy_jp_part_no !== part.jp_part_no && (
            <p className="text-[11px] text-white/45 truncate">
              Legacy: <span className="font-mono">{part.legacy_jp_part_no}</span>
            </p>
          )}
          {part.oe_part_no && (
            <p className="text-[11px] text-white/45 truncate">
              OE: <span className="font-mono">{part.oe_part_no}</span>
            </p>
          )}
          {part.brand && (
            <p className="text-[11px] text-white/45 truncate">{part.brand}{part.category ? ` · ${part.category}` : ""}</p>
          )}
          {part.mrp != null && (
            <p className="text-[11px] text-emerald-300 font-medium mt-0.5">MRP ₹{Number(part.mrp).toLocaleString("en-IN")}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const PartPicker = ({ jpPartNo, onChange }) => {
  const [query, setQuery] = useState("");
  const deb = useDebounced(query, 220);
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);
  const wrapRef = useRef(null);

  // If a JP is pre-set (edit mode), hydrate the selected card once
  useEffect(() => {
    let alive = true;
    if (jpPartNo && (!selected || selected.jp_part_no !== jpPartNo)) {
      axios.get(`${API}/task-panel/pickers/part/${encodeURIComponent(jpPartNo)}`)
        .then((r) => { if (alive) setSelected(r.data); })
        .catch(() => { /* swallow */ });
    } else if (!jpPartNo && selected) {
      setSelected(null);
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jpPartNo]);

  // Search
  useEffect(() => {
    let alive = true;
    if (!deb.trim() || selected) { setResults([]); return; }
    setLoading(true);
    axios.get(`${API}/task-panel/pickers/parts`, { params: { q: deb } })
      .then((r) => { if (alive) setResults(r.data.parts || []); })
      .catch(() => { if (alive) setResults([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [deb, selected]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const pick = (p) => {
    setSelected(p);
    setQuery("");
    setOpen(false);
    onChange(p.jp_part_no || null);
  };

  const clear = () => {
    setSelected(null);
    setQuery("");
    setResults([]);
    onChange(null);
  };

  if (selected) {
    const img = partImg(selected);
    return (
      <>
        <div
          className="flex items-center justify-between gap-2 bg-white/5 border border-white/15 rounded-md px-3 py-2"
          data-testid="task-create-selected-part"
        >
          <div className="flex items-center gap-2 min-w-0">
            {img ? (
              <button
                type="button"
                onClick={() => setZoomSrc(img)}
                className="shrink-0 w-10 h-10 rounded bg-white/5 border border-white/10 overflow-hidden hover:border-sky-400/60"
                title="Click to zoom"
                data-testid="task-create-selected-part-image"
              >
                <img src={img} alt={selected.part_name} className="w-full h-full object-contain" />
              </button>
            ) : (
              <div className="shrink-0 w-10 h-10 rounded bg-white/5 border border-white/10 flex items-center justify-center text-white/30">
                <Package className="w-4 h-4" />
              </div>
            )}
            <div className="min-w-0">
              <p className="text-sm text-white truncate">{selected.part_name || selected.jp_part_no}</p>
              <p className="text-[11px] text-white/55 truncate">
                <span className="font-mono">{selected.jp_part_no}</span>
                {selected.brand ? ` · ${selected.brand}` : ""}
                {selected.mrp != null ? ` · MRP ₹${Number(selected.mrp).toLocaleString("en-IN")}` : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={clear}
            className="text-white/45 hover:text-white"
            data-testid="task-create-clear-part"
            title="Clear part"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        {zoomSrc && <PartImageZoom src={zoomSrc} alt={selected.part_name} onClose={() => setZoomSrc(null)} />}
      </>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40 pointer-events-none" />
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search part by JP / OE / name…"
        className="w-full bg-white/5 border border-white/10 rounded-md pl-8 pr-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-red-500/60"
        data-testid="task-create-part-search"
        autoComplete="off"
      />
      {open && (loading || results.length > 0 || deb.trim()) && (
        <div
          className="absolute z-[90] mt-1 left-0 right-0 max-h-72 overflow-y-auto bg-[#0b1224] border border-white/15 rounded-md shadow-xl"
          data-testid="task-create-part-dropdown"
        >
          {loading ? (
            <div className="flex items-center justify-center py-3 text-white/50">
              <Loader2 className="w-4 h-4 animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <p className="px-3 py-2 text-xs text-white/45">No parts found</p>
          ) : (
            results.map((p) => {
              const img = partImg(p);
              return (
                <button
                  type="button"
                  key={p.jp_part_no}
                  onClick={() => pick(p)}
                  className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 last:border-b-0 flex items-center gap-2"
                  data-testid={`task-create-part-opt-${p.jp_part_no}`}
                >
                  {img ? (
                    <img src={img} alt="" className="w-9 h-9 rounded object-contain bg-white/5 border border-white/10 shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded bg-white/5 border border-white/10 flex items-center justify-center text-white/30 shrink-0">
                      <Package className="w-3.5 h-3.5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">{p.part_name || p.jp_part_no}</p>
                    <p className="text-[11px] text-white/50 truncate">
                      <span className="font-mono">{p.jp_part_no}</span>
                      {p.oe_part_no ? ` · OE ${p.oe_part_no}` : ""}
                      {p.brand ? ` · ${p.brand}` : ""}
                      {p.mrp != null ? ` · ₹${Number(p.mrp).toLocaleString("en-IN")}` : ""}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};
